import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Calendar,
  Clock,
  User,
  Phone,
  Car,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  LogOut,
  RefreshCw,
  Plus,
  Edit,
  CalendarDays,
  List,
  FileDown,
  FileUp,
  Upload,
  BookOpen
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import UserManual from './UserManual';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const MechanicDashboard = () => {
  const { user, logout, token } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [refreshing, setRefreshing] = useState(false);
  const [viewMode, setViewMode] = useState('day'); // 'day', 'week', 'month'
  const [showManual, setShowManual] = useState(false);
  
  // Modal states
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [importMode, setImportMode] = useState('update'); // 'update' or 'overwrite'
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [importing, setImporting] = useState(false);
  const [bookingSources, setBookingSources] = useState({});
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [vehicleSuggestions, setVehicleSuggestions] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [mechanics, setMechanics] = useState([]);
  const [bookingForm, setBookingForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_id: null,
    vehicle_type: 'car',
    reg_number: '',
    vehicle_make: '',
    vehicle_model: '',
    vehicle_id: null,
    service_id: '',
    date: '',
    time_slot: '',
    notes: '',
    booking_source: 'walk_in',
    booking_source_detail: '',
    assigned_mechanic: '',
    priority: 'normal'
  });

  // Handle CSV file selection and preview
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImportFile(file);
    
    // Read and preview CSV content
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result;
      const lines = content.split('\n');
      const headers = lines[0].split(/[\t,]/);
      
      const preview = [];
      for (let i = 1; i < Math.min(6, lines.length); i++) {
        if (lines[i].trim()) {
          const values = lines[i].split(/[\t,]/);
          const row = {};
          headers.forEach((header, idx) => {
            row[header.trim()] = values[idx]?.trim() || '';
          });
          preview.push(row);
        }
      }
      setImportPreview(preview);
    };
    reader.readAsText(file);
  };

  // Handle CSV import
  const handleImportCSV = async () => {
    if (!importFile) return;
    
    setImporting(true);
    
    try {
      const content = await importFile.text();
      
      const response = await axios.post(
        `${API}/bookings/import/csv?mode=${importMode}&send_emails=false`,
        content,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'text/plain'
          }
        }
      );
      
      const result = response.data;
      
      // Show results
      let message = `Import fullført!\n\n`;
      message += `Totalt rader: ${result.total}\n`;
      if (result.success > 0) message += `Nye bookinger: ${result.success}\n`;
      if (result.updated > 0) message += `Oppdaterte: ${result.updated}\n`;
      if (result.deleted_existing > 0) message += `Slettet eksisterende: ${result.deleted_existing}\n`;
      if (result.failed > 0) {
        message += `Feilet: ${result.failed}\n`;
        if (result.errors.length > 0) {
          message += `\nFeil:\n${result.errors.slice(0, 5).join('\n')}`;
          if (result.errors.length > 5) message += `\n... og ${result.errors.length - 5} flere feil`;
        }
      }
      
      alert(message);
      
      // Reset and close
      setShowImportModal(false);
      setImportFile(null);
      setImportPreview([]);
      fetchBookings();
      
    } catch (error) {
      console.error('Import feilet:', error);
      alert('Kunne ikke importere: ' + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
    }
  };

  // Download CSV template
  const downloadTemplate = () => {
    const template = `customer_name\tcustomer_email\tcustomer_phone\tdate\ttime_slot\tslot_number\tvehicle_type\treg_number\tvehicle_make\tvehicle_model\tbooking_source\tpriority\tnotes\tstatus
Ola Nordmann\tola@example.com\t99887766\t2025-12-20\t08:00\t1\tcar\tAB 12345\tToyota\tHilux\twalk_in\tnormal\tEU-kontroll\tconfirmed
Kari Hansen\tkari@example.com\t99112233\t2025-12-20\t09:00\t2\tvan\tCD 67890\tVolkswagen\tTransporter\tphone_inbound\thigh\tOljeskift\tpending`;
    
    const blob = new Blob([template], { type: 'text/tab-separated-values' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'booking_mal.tsv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const fetchBookings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data);
    } catch (error) {
      console.error('Feil ved henting av bookinger:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  const fetchServices = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/services`);
      // Services use 'published' instead of 'active', and 'title' instead of 'name'
      setServices(response.data.filter(s => s.published !== false));
    } catch (error) {
      console.error('Feil ved henting av tjenester:', error);
    }
  }, []);

  const fetchBookingSources = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/booking-sources`);
      setBookingSources(response.data);
    } catch (error) {
      console.error('Feil ved henting av bookingkilder:', error);
    }
  }, []);

  const fetchMechanics = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter to only mechanics and master_admin/company_owner
      const mechanicUsers = response.data.filter(u => 
        ['mechanic', 'master_admin', 'company_owner'].includes(u.role) && u.is_active
      );
      setMechanics(mechanicUsers);
    } catch (error) {
      // Non-admin users might not have access to users list
    }
  }, [token]);

  const searchCustomers = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setCustomerSuggestions([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/customers/search?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomerSuggestions(response.data);
    } catch (error) {
      console.error('Feil ved kundesøk:', error);
    }
  }, [token]);

  const searchVehicles = useCallback(async (customerId) => {
    if (!customerId) {
      setVehicleSuggestions([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/customers/${customerId}/vehicles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVehicleSuggestions(response.data);
    } catch (error) {
      console.error('Feil ved kjøretøysøk:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchBookings();
    fetchServices();
    fetchBookingSources();
    fetchMechanics();
    const interval = setInterval(fetchBookings, 60000);
    return () => clearInterval(interval);
  }, [fetchBookings, fetchServices, fetchBookingSources, fetchMechanics]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchBookings();
  };

  // Get dates for current view
  const getViewDates = () => {
    const dates = [];
    const start = new Date(selectedDate);
    
    if (viewMode === 'day') {
      dates.push(new Date(start));
    } else if (viewMode === 'week') {
      start.setDate(start.getDate() - start.getDay() + 1); // Start on Monday
      for (let i = 0; i < 7; i++) {
        dates.push(new Date(start));
        start.setDate(start.getDate() + 1);
      }
    } else if (viewMode === 'month') {
      start.setDate(1);
      const month = start.getMonth();
      while (start.getMonth() === month) {
        dates.push(new Date(start));
        start.setDate(start.getDate() + 1);
      }
    }
    return dates;
  };

  // Filter bookings for view
  const getBookingsForDate = (date) => {
    return bookings.filter(booking => {
      const bookingDate = new Date(booking.date);
      return bookingDate.toDateString() === date.toDateString();
    }).sort((a, b) => a.time_slot.localeCompare(b.time_slot));
  };

  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);
    if (viewMode === 'day') {
      newDate.setDate(newDate.getDate() + direction);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const formatDate = (date) => {
    const days = ['Søn', 'Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør'];
    const months = ['jan', 'feb', 'mar', 'apr', 'mai', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des'];
    return `${days[date.getDay()]} ${date.getDate()}. ${months[date.getMonth()]}`;
  };

  const formatFullDate = (date) => {
    const days = ['Søndag', 'Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag'];
    const months = ['januar', 'februar', 'mars', 'april', 'mai', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'desember'];
    return `${days[date.getDay()]} ${date.getDate()}. ${months[date.getMonth()]}`;
  };

  const isToday = (date) => date.toDateString() === new Date().toDateString();

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}/status`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      fetchBookings();
    } catch (error) {
      console.error('Feil ved oppdatering:', error);
    }
  };

  // Open modal for new booking
  const openNewBooking = (date = null) => {
    const bookingDate = date || selectedDate;
    setEditingBooking(null);
    setSelectedCustomer(null);
    setSelectedVehicle(null);
    setCustomerSuggestions([]);
    setVehicleSuggestions([]);
    setBookingForm({
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      customer_id: null,
      vehicle_type: 'car',
      reg_number: '',
      vehicle_make: '',
      vehicle_model: '',
      vehicle_id: null,
      service_id: services[0]?.id || '',
      date: bookingDate.toISOString().split('T')[0],
      time_slot: '08:00',
      notes: '',
      booking_source: 'walk_in',
      booking_source_detail: '',
      assigned_mechanic: user?.email || '',
      priority: 'normal'
    });
    setShowBookingModal(true);
  };

  // Open modal for editing
  const openEditBooking = (booking) => {
    setEditingBooking(booking);
    setSelectedCustomer(booking.customer_id ? { id: booking.customer_id } : null);
    setSelectedVehicle(booking.vehicle_id ? { id: booking.vehicle_id } : null);
    setCustomerSuggestions([]);
    setVehicleSuggestions([]);
    setBookingForm({
      customer_name: booking.customer_name || '',
      customer_email: booking.customer_email || '',
      customer_phone: booking.customer_phone || '',
      customer_id: booking.customer_id || null,
      vehicle_type: booking.vehicle_type || 'car',
      reg_number: booking.reg_number || '',
      vehicle_make: booking.vehicle_make || '',
      vehicle_model: booking.vehicle_model || '',
      vehicle_id: booking.vehicle_id || null,
      service_id: booking.service_id || '',
      date: booking.date ? new Date(booking.date).toISOString().split('T')[0] : '',
      time_slot: booking.time_slot || '',
      notes: booking.notes || '',
      booking_source: booking.booking_source || 'walk_in',
      booking_source_detail: booking.booking_source_detail || '',
      assigned_mechanic: booking.assigned_mechanic || '',
      priority: booking.priority || 'normal'
    });
    setShowBookingModal(true);
  };

  // Select customer from suggestions
  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setBookingForm(prev => ({
      ...prev,
      customer_name: customer.name,
      customer_email: customer.email,
      customer_phone: customer.phone,
      customer_id: customer.id
    }));
    setCustomerSuggestions([]);
    // Fetch vehicles for this customer
    searchVehicles(customer.id);
  };

  // Select vehicle from suggestions
  const selectVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
    setBookingForm(prev => ({
      ...prev,
      reg_number: vehicle.reg_number,
      vehicle_make: vehicle.make || '',
      vehicle_model: vehicle.model || '',
      vehicle_type: vehicle.vehicle_type || 'car',
      vehicle_id: vehicle.id
    }));
  };

  // Handle customer search input
  const handleCustomerSearch = (value) => {
    setBookingForm(prev => ({ ...prev, customer_name: value }));
    setSelectedCustomer(null);
    searchCustomers(value);
  };

  // Save booking
  const saveBooking = async () => {
    try {
      const selectedService = services.find(s => s.id === bookingForm.service_id);
      const payload = {
        customer_name: bookingForm.customer_name,
        customer_email: bookingForm.customer_email,
        customer_phone: bookingForm.customer_phone,
        customer_id: bookingForm.customer_id,
        vehicle_type: bookingForm.vehicle_type,
        reg_number: bookingForm.reg_number,
        vehicle_make: bookingForm.vehicle_make,
        vehicle_model: bookingForm.vehicle_model,
        vehicle_id: bookingForm.vehicle_id,
        service_id: bookingForm.service_id,
        service_name: selectedService?.title || selectedService?.name || '',
        date: bookingForm.date,
        time_slot: bookingForm.time_slot,
        notes: bookingForm.notes,
        booking_source: bookingForm.booking_source,
        booking_source_detail: bookingForm.booking_source_detail,
        assigned_mechanic: bookingForm.assigned_mechanic,
        priority: bookingForm.priority
      };

      if (editingBooking) {
        await axios.put(`${API}/bookings/${editingBooking.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/bookings`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      setShowBookingModal(false);
      setSelectedCustomer(null);
      setSelectedVehicle(null);
      fetchBookings();
    } catch (error) {
      console.error('Feil ved lagring:', error);
      alert('Kunne ikke lagre booking: ' + (error.response?.data?.detail || error.message));
    }
  };

  // PDF Export function
  const exportToPDF = () => {
    const dayBookings = getBookingsForDate(selectedDate);
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(24);
    doc.setTextColor(220, 38, 38); // Red color
    doc.text('Sand-tec AS', 14, 20);
    
    doc.setFontSize(12);
    doc.setTextColor(100);
    doc.text('Verksted - Dagsrapport', 14, 28);
    
    // Date
    doc.setFontSize(16);
    doc.setTextColor(0);
    doc.text(formatFullDate(selectedDate), 14, 42);
    
    // Summary
    doc.setFontSize(11);
    doc.setTextColor(80);
    const pending = dayBookings.filter(b => b.status === 'pending').length;
    const confirmed = dayBookings.filter(b => b.status === 'confirmed').length;
    const completed = dayBookings.filter(b => b.status === 'completed').length;
    const cancelled = dayBookings.filter(b => b.status === 'cancelled').length;
    
    doc.text(`Totalt: ${dayBookings.length} bookinger  |  Venter: ${pending}  |  Bekreftet: ${confirmed}  |  Fullført: ${completed}  |  Kansellert: ${cancelled}`, 14, 52);
    
    if (dayBookings.length === 0) {
      doc.setFontSize(14);
      doc.setTextColor(150);
      doc.text('Ingen bookinger denne dagen', 14, 70);
    } else {
      // Table with bookings
      const tableData = dayBookings.map(booking => [
        booking.time_slot,
        booking.customer_name,
        booking.customer_phone,
        `${booking.vehicle_type || ''}\n${booking.registration_number || ''}`,
        booking.service_name || '',
        getStatusText(booking.status),
        booking.notes || ''
      ]);
      
      autoTable(doc, {
        startY: 60,
        head: [['Tid', 'Kunde', 'Telefon', 'Kjøretøy', 'Tjeneste', 'Status', 'Merknad']],
        body: tableData,
        theme: 'striped',
        headStyles: {
          fillColor: [220, 38, 38],
          textColor: 255,
          fontSize: 10,
          fontStyle: 'bold'
        },
        bodyStyles: {
          fontSize: 9,
          textColor: 50
        },
        columnStyles: {
          0: { cellWidth: 15 },
          1: { cellWidth: 30 },
          2: { cellWidth: 25 },
          3: { cellWidth: 25 },
          4: { cellWidth: 35 },
          5: { cellWidth: 20 },
          6: { cellWidth: 'auto' }
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        }
      });
    }
    
    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(
        `Generert: ${new Date().toLocaleString('no-NO')} | Side ${i} av ${pageCount}`,
        14,
        doc.internal.pageSize.height - 10
      );
    }
    
    // Download
    const dateStr = selectedDate.toISOString().split('T')[0];
    doc.save(`Sand-tec_Bookinger_${dateStr}.pdf`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-600';
      case 'completed': return 'bg-blue-600';
      case 'cancelled': return 'bg-red-600';
      case 'no_show': return 'bg-gray-600';
      default: return 'bg-yellow-600';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'confirmed': return 'Bekreftet';
      case 'completed': return 'Fullført';
      case 'cancelled': return 'Kansellert';
      case 'no_show': return 'Ikke møtt';
      case 'pending': return 'Venter';
      default: return status;
    }
  };

  const timeSlots = [
    '08:00', '08:30', '09:00', '09:30', '10:00', '10:30',
    '11:00', '11:30', '12:00', '12:30', '13:00', '13:30',
    '14:00', '14:30', '15:00', '15:30', '16:00'
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-3xl font-bold text-gray-700">Laster...</div>
      </div>
    );
  }

  const viewDates = getViewDates();

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-red-600 text-white p-6 shadow-lg">
        <div className="container mx-auto flex flex-col lg:flex-row items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold">Sand-tec Verksted</h1>
            <p className="text-xl mt-1 opacity-90">Hei, {user?.full_name}</p>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={() => openNewBooking()}
              className="bg-white text-red-600 hover:bg-gray-100 text-xl px-8 py-6 h-auto font-bold"
              data-testid="new-booking-btn"
            >
              <Plus className="w-6 h-6 mr-3" />
              Ny booking
            </Button>
            <Button
              onClick={exportToPDF}
              className="bg-green-600 text-white hover:bg-green-700 text-xl px-8 py-6 h-auto font-bold"
              data-testid="export-pdf-btn"
            >
              <FileDown className="w-6 h-6 mr-3" />
              Eksporter PDF
            </Button>
            <Button
              onClick={() => setShowImportModal(true)}
              className="bg-blue-600 text-white hover:bg-blue-700 text-xl px-8 py-6 h-auto font-bold"
              data-testid="import-csv-btn"
            >
              <Upload className="w-6 h-6 mr-3" />
              Importer CSV
            </Button>
            <Button
              onClick={() => setShowManual(true)}
              variant="outline"
              className="border-blue-400 text-blue-400 hover:bg-blue-400/20 text-xl px-8 py-6 h-auto font-bold"
              data-testid="help-btn"
            >
              <BookOpen className="w-6 h-6 mr-3" />
              Hjelp
            </Button>
            <Button
              onClick={logout}
              variant="outline"
              className="border-white text-white hover:bg-white/20 text-xl px-8 py-6 h-auto font-bold"
              data-testid="mechanic-logout-btn"
            >
              <LogOut className="w-6 h-6 mr-3" />
              Logg ut
            </Button>
          </div>
        </div>
      </header>

      {/* User Manual Dialog */}
      <Dialog open={showManual} onOpenChange={setShowManual}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-2xl">Brukermanual</DialogTitle>
          </DialogHeader>
          <UserManual />
        </DialogContent>
      </Dialog>

      <main className="container mx-auto p-4 md:p-8">
        {/* View Mode Toggle */}
        <Card className="mb-6 border-2 border-gray-300">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Button
                onClick={() => setViewMode('day')}
                className={`text-xl px-8 py-6 h-auto font-bold ${viewMode === 'day' ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              >
                <List className="w-6 h-6 mr-2" />
                Dag
              </Button>
              <Button
                onClick={() => setViewMode('week')}
                className={`text-xl px-8 py-6 h-auto font-bold ${viewMode === 'week' ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              >
                <CalendarDays className="w-6 h-6 mr-2" />
                Uke
              </Button>
              <Button
                onClick={() => setViewMode('month')}
                className={`text-xl px-8 py-6 h-auto font-bold ${viewMode === 'month' ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              >
                <Calendar className="w-6 h-6 mr-2" />
                Måned
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Date Navigation */}
        <Card className="mb-8 border-2 border-gray-300">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <Button
                onClick={() => navigateDate(-1)}
                className="w-full md:w-auto bg-gray-700 hover:bg-gray-800 text-white text-2xl px-10 py-8 h-auto font-bold"
              >
                <ChevronLeft className="w-10 h-10 mr-2" />
                {viewMode === 'day' ? 'Forrige dag' : viewMode === 'week' ? 'Forrige uke' : 'Forrige måned'}
              </Button>

              <div className="text-center flex-1">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-800">
                  {viewMode === 'day' && formatFullDate(selectedDate)}
                  {viewMode === 'week' && `Uke ${Math.ceil((selectedDate.getDate() - selectedDate.getDay() + 1) / 7)}`}
                  {viewMode === 'month' && selectedDate.toLocaleDateString('no-NO', { month: 'long', year: 'numeric' })}
                </h2>
                {!isToday(selectedDate) && (
                  <Button onClick={goToToday} variant="link" className="text-xl text-red-600 mt-2">
                    Gå til i dag
                  </Button>
                )}
              </div>

              <Button
                onClick={() => navigateDate(1)}
                className="w-full md:w-auto bg-gray-700 hover:bg-gray-800 text-white text-2xl px-10 py-8 h-auto font-bold"
              >
                {viewMode === 'day' ? 'Neste dag' : viewMode === 'week' ? 'Neste uke' : 'Neste måned'}
                <ChevronRight className="w-10 h-10 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Refresh Button */}
        <div className="mb-6 flex justify-end">
          <Button
            onClick={handleRefresh}
            disabled={refreshing}
            className="bg-blue-600 hover:bg-blue-700 text-white text-xl px-8 py-4 h-auto"
          >
            <RefreshCw className={`w-6 h-6 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Oppdater
          </Button>
        </div>

        {/* Calendar View */}
        {viewMode !== 'day' ? (
          <div className={`grid ${viewMode === 'week' ? 'grid-cols-7' : 'grid-cols-7'} gap-2`}>
            {/* Day headers for week/month view */}
            {['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'].map(day => (
              <div key={day} className="text-center font-bold text-xl text-gray-600 p-2">
                {day}
              </div>
            ))}
            
            {/* Calendar cells */}
            {viewMode === 'month' && (
              // Add empty cells for days before month starts
              [...Array(((viewDates[0]?.getDay() || 0) + 6) % 7)].map((_, i) => (
                <div key={`empty-${i}`} className="p-2"></div>
              ))
            )}
            
            {viewDates.map(date => {
              const dayBookings = getBookingsForDate(date);
              const today = isToday(date);
              
              return (
                <Card 
                  key={date.toISOString()} 
                  className={`border-2 min-h-[120px] cursor-pointer hover:border-red-400 transition-colors ${
                    today ? 'border-green-500 bg-green-50' : 'border-gray-300'
                  }`}
                  onClick={() => {
                    setSelectedDate(date);
                    setViewMode('day');
                  }}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-lg font-bold ${today ? 'text-green-700' : 'text-gray-700'}`}>
                        {date.getDate()}
                      </span>
                      {dayBookings.length > 0 && (
                        <Badge className="bg-red-600 text-white text-sm">
                          {dayBookings.length}
                        </Badge>
                      )}
                    </div>
                    <div className="space-y-1">
                      {dayBookings.slice(0, 3).map(booking => (
                        <div 
                          key={booking.id}
                          className={`text-xs p-1 rounded truncate ${getStatusColor(booking.status)} text-white`}
                        >
                          {booking.time_slot} - {booking.customer_name}
                        </div>
                      ))}
                      {dayBookings.length > 3 && (
                        <div className="text-xs text-gray-500">+{dayBookings.length - 3} mer</div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          /* Day View - Detailed List */
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Card className="border-2 border-gray-300">
                <CardContent className="p-6 text-center">
                  <div className="text-5xl font-bold text-gray-800">{getBookingsForDate(selectedDate).length}</div>
                  <div className="text-xl text-gray-600 mt-2">Totalt</div>
                </CardContent>
              </Card>
              <Card className="border-2 border-yellow-400 bg-yellow-50">
                <CardContent className="p-6 text-center">
                  <div className="text-5xl font-bold text-yellow-700">
                    {getBookingsForDate(selectedDate).filter(b => b.status === 'pending').length}
                  </div>
                  <div className="text-xl text-yellow-700 mt-2">Venter</div>
                </CardContent>
              </Card>
              <Card className="border-2 border-green-400 bg-green-50">
                <CardContent className="p-6 text-center">
                  <div className="text-5xl font-bold text-green-700">
                    {getBookingsForDate(selectedDate).filter(b => b.status === 'confirmed').length}
                  </div>
                  <div className="text-xl text-green-700 mt-2">Bekreftet</div>
                </CardContent>
              </Card>
              <Card className="border-2 border-blue-400 bg-blue-50">
                <CardContent className="p-6 text-center">
                  <div className="text-5xl font-bold text-blue-700">
                    {getBookingsForDate(selectedDate).filter(b => b.status === 'completed').length}
                  </div>
                  <div className="text-xl text-blue-700 mt-2">Fullført</div>
                </CardContent>
              </Card>
            </div>

            {/* Bookings List */}
            {getBookingsForDate(selectedDate).length === 0 ? (
              <Card className="border-2 border-gray-300">
                <CardContent className="p-12 text-center">
                  <Calendar className="w-20 h-20 mx-auto text-gray-400 mb-4" />
                  <p className="text-3xl text-gray-500 mb-6">Ingen bookinger denne dagen</p>
                  <Button
                    onClick={() => openNewBooking()}
                    className="bg-red-600 hover:bg-red-700 text-white text-2xl px-10 py-6 h-auto font-bold"
                  >
                    <Plus className="w-8 h-8 mr-3" />
                    Legg til booking
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {getBookingsForDate(selectedDate).map((booking) => (
                  <Card 
                    key={booking.id} 
                    className={`border-4 ${
                      booking.status === 'completed' ? 'border-blue-400 bg-blue-50' :
                      booking.status === 'confirmed' ? 'border-green-400 bg-green-50' :
                      booking.status === 'cancelled' ? 'border-red-400 bg-red-50 opacity-60' :
                      'border-yellow-400 bg-yellow-50'
                    }`}
                  >
                    <CardContent className="p-6">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                        {/* Time and Status */}
                        <div className="flex items-center gap-6">
                          <div className="bg-white rounded-xl p-4 shadow-md text-center min-w-[120px]">
                            <Clock className="w-8 h-8 mx-auto text-gray-600 mb-2" />
                            <div className="text-3xl font-bold text-gray-800">{booking.time_slot}</div>
                          </div>
                          <Badge className={`${getStatusColor(booking.status)} text-white text-xl px-6 py-3`}>
                            {getStatusText(booking.status)}
                          </Badge>
                        </div>

                        {/* Customer Info */}
                        <div className="flex-1 space-y-3">
                          <div className="flex items-center gap-3">
                            <User className="w-8 h-8 text-gray-600" />
                            <span className="text-2xl font-bold text-gray-800">{booking.customer_name}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <Phone className="w-8 h-8 text-gray-600" />
                            <a href={`tel:${booking.customer_phone}`} className="text-2xl text-blue-600 underline font-medium">
                              {booking.customer_phone}
                            </a>
                          </div>
                          <div className="flex items-center gap-3">
                            <Car className="w-8 h-8 text-gray-600" />
                            <span className="text-xl text-gray-700">
                              {booking.vehicle_type === 'car' ? 'Personbil' : 
                               booking.vehicle_type === 'van' ? 'Varebil' :
                               booking.vehicle_type === 'truck' ? 'Lastebil' :
                               booking.vehicle_type} 
                              {booking.reg_number && ` - ${booking.reg_number}`}
                              {booking.vehicle_make && ` (${booking.vehicle_make}${booking.vehicle_model ? ' ' + booking.vehicle_model : ''})`}
                            </span>
                          </div>
                          <div className="bg-white rounded-lg p-3 mt-2">
                            <span className="text-xl font-medium text-gray-800">{booking.service_name}</span>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col gap-4 min-w-[200px]">
                          {/* Edit button - always visible */}
                          <Button
                            onClick={() => openEditBooking(booking)}
                            className="bg-gray-600 hover:bg-gray-700 text-white text-xl px-8 py-6 h-auto font-bold w-full"
                          >
                            <Edit className="w-8 h-8 mr-3" />
                            Rediger
                          </Button>
                          
                          {booking.status !== 'cancelled' && booking.status !== 'completed' && (
                            <>
                              {booking.status === 'pending' && (
                                <Button
                                  onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                                  className="bg-green-600 hover:bg-green-700 text-white text-xl px-8 py-6 h-auto font-bold w-full"
                                >
                                  <CheckCircle className="w-8 h-8 mr-3" />
                                  Bekreft
                                </Button>
                              )}
                              {booking.status === 'confirmed' && (
                                <Button
                                  onClick={() => updateBookingStatus(booking.id, 'completed')}
                                  className="bg-blue-600 hover:bg-blue-700 text-white text-xl px-8 py-6 h-auto font-bold w-full"
                                >
                                  <CheckCircle className="w-8 h-8 mr-3" />
                                  Fullført
                                </Button>
                              )}
                              <Button
                                onClick={() => updateBookingStatus(booking.id, 'no_show')}
                                variant="outline"
                                className="border-2 border-gray-400 text-gray-700 hover:bg-gray-100 text-xl px-8 py-6 h-auto font-bold w-full"
                              >
                                <AlertCircle className="w-8 h-8 mr-3" />
                                Ikke møtt
                              </Button>
                              <Button
                                onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                                variant="outline"
                                className="border-2 border-red-400 text-red-600 hover:bg-red-50 text-xl px-8 py-6 h-auto font-bold w-full"
                              >
                                <XCircle className="w-8 h-8 mr-3" />
                                Kanseller
                              </Button>
                            </>
                          )}
                        </div>
                      </div>

                      {booking.notes && (
                        <div className="mt-4 p-4 bg-white rounded-lg border-2 border-gray-200">
                          <p className="text-xl text-gray-700"><strong>Merknad:</strong> {booking.notes}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {/* Booking Modal */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-3xl font-bold">
              {editingBooking ? 'Rediger booking' : 'Ny booking'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Customer Info with Search */}
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-gray-800 border-b pb-2">Kundeinformasjon</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 relative">
                  <Label className="text-lg font-bold text-gray-900">Kundenavn *</Label>
                  <Input
                    value={bookingForm.customer_name}
                    onChange={(e) => handleCustomerSearch(e.target.value)}
                    className="text-lg p-4 h-auto border-2 border-gray-800 bg-white text-gray-900 placeholder:text-gray-500 focus:border-red-600 focus:ring-2 focus:ring-red-600"
                    placeholder="Søk eller skriv kundenavn"
                    data-testid="customer-name-input"
                  />
                  {customerSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full bg-white border-2 border-gray-400 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {customerSuggestions.map(customer => (
                        <div
                          key={customer.id}
                          onClick={() => selectCustomer(customer)}
                          className="p-3 hover:bg-gray-100 cursor-pointer border-b"
                        >
                          <div className="font-bold">{customer.name}</div>
                          <div className="text-sm text-gray-600">{customer.phone} • {customer.email}</div>
                          {customer.company_name && (
                            <div className="text-sm text-blue-600">{customer.company_name}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {selectedCustomer && (
                    <span className="text-sm text-green-600">✓ Eksisterende kunde</span>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-lg font-bold text-gray-900">Telefon *</Label>
                  <Input
                    value={bookingForm.customer_phone}
                    onChange={(e) => setBookingForm({...bookingForm, customer_phone: e.target.value})}
                    className="text-lg p-4 h-auto border-2 border-gray-800 bg-white text-gray-900 placeholder:text-gray-500 focus:border-red-600 focus:ring-2 focus:ring-red-600"
                    placeholder="99 99 99 99"
                    data-testid="customer-phone-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-lg font-bold text-gray-900">E-post</Label>
                <Input
                  type="email"
                  value={bookingForm.customer_email}
                  onChange={(e) => setBookingForm({...bookingForm, customer_email: e.target.value})}
                  className="text-lg p-4 h-auto border-2 border-gray-800 bg-white text-gray-900 placeholder:text-gray-500 focus:border-red-600 focus:ring-2 focus:ring-red-600"
                  placeholder="ola@eksempel.no"
                  data-testid="customer-email-input"
                />
              </div>
            </div>

            {/* Vehicle Info */}
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-gray-800 border-b pb-2">Kjøretøy</h3>
              
              {vehicleSuggestions.length > 0 && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <Label className="text-lg font-bold text-blue-800 mb-2 block">Kundens kjøretøy</Label>
                  <div className="flex flex-wrap gap-2">
                    {vehicleSuggestions.map(vehicle => (
                      <Button
                        key={vehicle.id}
                        type="button"
                        variant={selectedVehicle?.id === vehicle.id ? "default" : "outline"}
                        onClick={() => selectVehicle(vehicle)}
                        className={`${selectedVehicle?.id === vehicle.id ? 'bg-blue-600' : 'bg-white'}`}
                      >
                        {vehicle.reg_number} {vehicle.make && `(${vehicle.make})`}
                      </Button>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setSelectedVehicle(null);
                        setBookingForm(prev => ({...prev, reg_number: '', vehicle_make: '', vehicle_model: '', vehicle_id: null}));
                      }}
                      className="border-dashed"
                    >
                      + Nytt kjøretøy
                    </Button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-lg font-bold text-gray-900">Kjennemerke *</Label>
                  <Input
                    value={bookingForm.reg_number}
                    onChange={(e) => setBookingForm({...bookingForm, reg_number: e.target.value.toUpperCase(), vehicle_id: null})}
                    className="text-lg p-4 h-auto border-2 border-gray-800 bg-white text-gray-900 placeholder:text-gray-500 focus:border-red-600 focus:ring-2 focus:ring-red-600"
                    placeholder="AB 12345"
                    data-testid="vehicle-reg-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-lg font-bold text-gray-900">Merke</Label>
                  <Input
                    value={bookingForm.vehicle_make}
                    onChange={(e) => setBookingForm({...bookingForm, vehicle_make: e.target.value})}
                    className="text-lg p-4 h-auto border-2 border-gray-800 bg-white text-gray-900 placeholder:text-gray-500 focus:border-red-600 focus:ring-2 focus:ring-red-600"
                    placeholder="Toyota"
                    data-testid="vehicle-make-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-lg font-bold text-gray-900">Modell</Label>
                  <Input
                    value={bookingForm.vehicle_model}
                    onChange={(e) => setBookingForm({...bookingForm, vehicle_model: e.target.value})}
                    className="text-lg p-4 h-auto border-2 border-gray-800 bg-white text-gray-900 placeholder:text-gray-500 focus:border-red-600 focus:ring-2 focus:ring-red-600"
                    placeholder="Hilux"
                    data-testid="vehicle-model-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-lg font-bold text-gray-900">Kjøretøytype *</Label>
                <select
                  value={bookingForm.vehicle_type}
                  onChange={(e) => setBookingForm({...bookingForm, vehicle_type: e.target.value})}
                  className="w-full text-lg p-3 border-2 border-gray-800 rounded-lg bg-white text-gray-900 focus:border-red-600 focus:ring-2 focus:ring-red-600 focus:outline-none"
                  data-testid="vehicle-type-select"
                >
                  <option value="car">Personbil</option>
                  <option value="van">Varebil</option>
                  <option value="truck">Lastebil</option>
                  <option value="motorcycle">Motorsykkel</option>
                  <option value="trailer">Tilhenger</option>
                  <option value="other">Annet</option>
                </select>
              </div>
            </div>

            {/* Service and Time */}
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-gray-800 border-b pb-2">Tjeneste og tid</h3>
              
              <div className="space-y-2">
                <Label className="text-lg font-bold text-gray-900">Tjeneste *</Label>
                <select
                  value={bookingForm.service_id}
                  onChange={(e) => setBookingForm({...bookingForm, service_id: e.target.value})}
                  className="w-full text-lg p-3 border-2 border-gray-800 rounded-lg bg-white text-gray-900 focus:border-red-600 focus:ring-2 focus:ring-red-600 focus:outline-none"
                  data-testid="service-select"
                >
                  <option value="">Velg tjeneste</option>
                  {services.map(service => (
                    <option key={service.id} value={service.id}>
                      {service.title || service.name} - {service.price}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-lg font-bold text-gray-900">Dato *</Label>
                  <Input
                    type="date"
                    value={bookingForm.date}
                    onChange={(e) => setBookingForm({...bookingForm, date: e.target.value})}
                    className="text-lg p-4 h-auto border-2 border-gray-800 bg-white text-gray-900 focus:border-red-600 focus:ring-2 focus:ring-red-600"
                    data-testid="date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-lg font-bold text-gray-900">Tidspunkt *</Label>
                  <select
                    value={bookingForm.time_slot}
                    onChange={(e) => setBookingForm({...bookingForm, time_slot: e.target.value})}
                    className="w-full text-lg p-3 border-2 border-gray-800 rounded-lg bg-white text-gray-900 focus:border-red-600 focus:ring-2 focus:ring-red-600 focus:outline-none"
                    data-testid="time-slot-select"
                  >
                    {timeSlots.map(slot => (
                      <option key={slot} value={slot}>{slot}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Source and Assignment */}
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-gray-800 border-b pb-2">Kilde og tildeling</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-lg font-bold text-gray-900">Bookingkilde *</Label>
                  <select
                    value={bookingForm.booking_source}
                    onChange={(e) => setBookingForm({...bookingForm, booking_source: e.target.value})}
                    className="w-full text-lg p-3 border-2 border-gray-800 rounded-lg bg-white text-gray-900 focus:border-red-600 focus:ring-2 focus:ring-red-600 focus:outline-none"
                    data-testid="booking-source-select"
                  >
                    {Object.entries(bookingSources).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-lg font-bold text-gray-900">Prioritet</Label>
                  <select
                    value={bookingForm.priority}
                    onChange={(e) => setBookingForm({...bookingForm, priority: e.target.value})}
                    className="w-full text-lg p-3 border-2 border-gray-800 rounded-lg bg-white text-gray-900 focus:border-red-600 focus:ring-2 focus:ring-red-600 focus:outline-none"
                    data-testid="priority-select"
                  >
                    <option value="normal">Normal</option>
                    <option value="high">Høy</option>
                    <option value="urgent">Haster</option>
                  </select>
                </div>
              </div>

              {bookingForm.booking_source === 'other' && (
                <div className="space-y-2">
                  <Label className="text-lg font-bold text-gray-900">Spesifiser kilde</Label>
                  <Input
                    value={bookingForm.booking_source_detail}
                    onChange={(e) => setBookingForm({...bookingForm, booking_source_detail: e.target.value})}
                    className="text-lg p-4 h-auto border-2 border-gray-800 bg-white text-gray-900 placeholder:text-gray-500 focus:border-red-600 focus:ring-2 focus:ring-red-600"
                    placeholder="Beskriv hvor bookingen kommer fra..."
                    data-testid="source-detail-input"
                  />
                </div>
              )}

              {mechanics.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-lg font-bold text-gray-900">Tildelt mekaniker</Label>
                  <select
                    value={bookingForm.assigned_mechanic}
                    onChange={(e) => setBookingForm({...bookingForm, assigned_mechanic: e.target.value})}
                    className="w-full text-lg p-3 border-2 border-gray-800 rounded-lg bg-white text-gray-900 focus:border-red-600 focus:ring-2 focus:ring-red-600 focus:outline-none"
                    data-testid="mechanic-select"
                  >
                    <option value="">Ikke tildelt</option>
                    {mechanics.map(mech => (
                      <option key={mech.id} value={mech.email}>
                        {mech.full_name || mech.email}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-lg font-bold text-gray-900">Merknad</Label>
              <Textarea
                value={bookingForm.notes}
                onChange={(e) => setBookingForm({...bookingForm, notes: e.target.value})}
                className="text-lg p-4 min-h-[80px] border-2 border-gray-800 bg-white text-gray-900 placeholder:text-gray-500 focus:border-red-600 focus:ring-2 focus:ring-red-600"
                placeholder="Eventuelle kommentarer..."
                data-testid="notes-textarea"
              />
            </div>
          </div>

          <DialogFooter className="gap-4">
            <Button
              onClick={() => setShowBookingModal(false)}
              variant="outline"
              className="text-xl px-8 py-6 h-auto font-bold border-2 border-gray-800 text-gray-800 bg-white hover:bg-gray-100"
            >
              Avbryt
            </Button>
            <Button
              onClick={saveBooking}
              className="bg-red-600 hover:bg-red-700 text-white text-xl px-8 py-6 h-auto font-bold"
            >
              {editingBooking ? 'Lagre endringer' : 'Opprett booking'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Import Modal */}
      <Dialog open={showImportModal} onOpenChange={(open) => {
        setShowImportModal(open);
        if (!open) {
          setImportFile(null);
          setImportPreview([]);
        }
      }}>
        <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-3xl font-bold flex items-center gap-3">
              <Upload className="w-8 h-8 text-blue-600" />
              Importer bookinger fra CSV
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Mode Selection */}
            <div className="space-y-4">
              <Label className="text-xl font-bold text-gray-900">Importmodus</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                  onClick={() => setImportMode('update')}
                  className={`p-6 border-3 rounded-xl cursor-pointer transition-all ${
                    importMode === 'update' 
                      ? 'border-blue-600 bg-blue-50' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      checked={importMode === 'update'}
                      onChange={() => setImportMode('update')}
                      className="mt-1 w-5 h-5"
                    />
                    <div>
                      <p className="text-xl font-bold text-gray-900">Oppdater</p>
                      <p className="text-lg text-gray-600 mt-1">
                        Legger til nye bookinger. Oppdaterer eksisterende hvis dato/tid/plass matcher.
                      </p>
                    </div>
                  </div>
                </div>
                <div
                  onClick={() => setImportMode('overwrite')}
                  className={`p-6 border-3 rounded-xl cursor-pointer transition-all ${
                    importMode === 'overwrite' 
                      ? 'border-red-600 bg-red-50' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      checked={importMode === 'overwrite'}
                      onChange={() => setImportMode('overwrite')}
                      className="mt-1 w-5 h-5"
                    />
                    <div>
                      <p className="text-xl font-bold text-gray-900">Overskriv</p>
                      <p className="text-lg text-gray-600 mt-1">
                        Sletter alle eksisterende bookinger for datoene i CSV, deretter legger til nye.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* File Upload */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-xl font-bold text-gray-900">Velg CSV-fil</Label>
                <Button
                  onClick={downloadTemplate}
                  variant="outline"
                  className="text-lg border-2 border-blue-600 text-blue-600 hover:bg-blue-50"
                >
                  <FileDown className="w-5 h-5 mr-2" />
                  Last ned mal
                </Button>
              </div>
              
              <div className="border-3 border-dashed border-gray-400 rounded-xl p-8 text-center hover:border-blue-500 transition-colors">
                <input
                  type="file"
                  accept=".csv,.tsv,.txt"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="csv-file-input"
                  data-testid="csv-file-input"
                />
                <label htmlFor="csv-file-input" className="cursor-pointer">
                  <FileUp className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                  <p className="text-xl text-gray-700 font-medium">
                    {importFile ? importFile.name : 'Klikk for å velge fil'}
                  </p>
                  <p className="text-lg text-gray-500 mt-2">
                    Støtter CSV og TSV-filer (tab-separert)
                  </p>
                </label>
              </div>
            </div>

            {/* Expected Format Info */}
            <div className="bg-gray-100 rounded-xl p-6">
              <p className="text-lg font-bold text-gray-800 mb-3">Forventet format (kolonner):</p>
              <code className="text-sm bg-white p-3 rounded block overflow-x-auto">
                customer_name, customer_email, customer_phone, date, time_slot, slot_number, vehicle_type, notes, status
              </code>
              <p className="text-sm text-gray-600 mt-3">
                * date må være i format YYYY-MM-DD (f.eks. 2025-12-20)<br/>
                * time_slot må være i format HH:MM (f.eks. 08:00)<br/>
                * status kan være: pending, confirmed, completed, cancelled
              </p>
            </div>

            {/* Preview */}
            {importPreview.length > 0 && (
              <div className="space-y-3">
                <Label className="text-xl font-bold text-gray-900">Forhåndsvisning (første 5 rader)</Label>
                <div className="overflow-x-auto border-2 border-gray-300 rounded-xl">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-3 text-left font-bold">Navn</th>
                        <th className="p-3 text-left font-bold">Telefon</th>
                        <th className="p-3 text-left font-bold">Dato</th>
                        <th className="p-3 text-left font-bold">Tid</th>
                        <th className="p-3 text-left font-bold">Kjøretøy</th>
                        <th className="p-3 text-left font-bold">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.map((row, idx) => (
                        <tr key={idx} className="border-t border-gray-200">
                          <td className="p-3">{row.customer_name}</td>
                          <td className="p-3">{row.customer_phone}</td>
                          <td className="p-3">{row.date}</td>
                          <td className="p-3">{row.time_slot}</td>
                          <td className="p-3">{row.vehicle_type}</td>
                          <td className="p-3">{row.status || 'confirmed'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Warning for overwrite mode */}
            {importMode === 'overwrite' && (
              <div className="bg-red-100 border-2 border-red-400 rounded-xl p-4 flex items-start gap-3">
                <AlertCircle className="w-8 h-8 text-red-600 flex-shrink-0" />
                <div>
                  <p className="text-lg font-bold text-red-800">Advarsel!</p>
                  <p className="text-lg text-red-700">
                    Overskriv-modus vil slette ALLE eksisterende bookinger for datoene som finnes i CSV-filen. 
                    Denne handlingen kan ikke angres.
                  </p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="gap-4">
            <Button
              onClick={() => {
                setShowImportModal(false);
                setImportFile(null);
                setImportPreview([]);
              }}
              variant="outline"
              className="text-xl px-8 py-6 h-auto font-bold border-2 border-gray-800 text-gray-800 bg-white hover:bg-gray-100"
            >
              Avbryt
            </Button>
            <Button
              onClick={handleImportCSV}
              disabled={!importFile || importing}
              className={`text-xl px-8 py-6 h-auto font-bold ${
                importMode === 'overwrite' 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : 'bg-blue-600 hover:bg-blue-700'
              } text-white disabled:opacity-50`}
              data-testid="confirm-import-btn"
            >
              {importing ? (
                <>
                  <RefreshCw className="w-6 h-6 mr-3 animate-spin" />
                  Importerer...
                </>
              ) : (
                <>
                  <Upload className="w-6 h-6 mr-3" />
                  {importMode === 'overwrite' ? 'Overskriv og importer' : 'Importer'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-gray-800 text-white p-6 mt-12">
        <div className="container mx-auto text-center">
          <p className="text-xl">Sand-tec AS | Verksted Dashboard</p>
        </div>
      </footer>
    </div>
  );
};

export default MechanicDashboard;
